/**
 * Hook to fetch current user profile with all fields
 * Uses edge function to comply with security policies (no direct DB calls)
 *
 * @returns {Object} profile data, loading state, error state, and refetch function
 */

import { useEffect, useState, useCallback } from 'react';
import { invokeEdgeFunction } from '@/lib/supabase-functions-wrapper';
import { componentLogger } from '@/lib/logging';
import type { UserProfile, Company } from '@/lib/types/core';

interface UserProfileResponse {
  message: string;
  profile: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    language: string | null;
    company_id: string | null;
    app_role: string;
    status: string | null;
    is_representative: boolean | null;
    title: string | null;
    company?: {
      id: string;
      name: string;
      type: string;
      default_language: string | null;
      logo_attachment_id: string | null;
    } | null;
  };
}

interface UseCurrentUserProfileReturn {
  profile: UserProfile | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCurrentUserProfile(): UseCurrentUserProfileReturn {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      componentLogger.debug('useCurrentUserProfile', 'Fetching current user profile');

      const { data, error: fetchError } = await invokeEdgeFunction<UserProfileResponse>(
        'get-user-profile-with-language'
      );

      if (fetchError) {
        throw new Error((fetchError as { message?: string }).message || 'Failed to fetch user profile');
      }

      if (!data?.profile) {
        throw new Error('No profile data returned from edge function');
      }

      // Transform edge function response to UserProfile type
      const userProfile: UserProfile = {
        id: data.profile.id,
        email: data.profile.email,
        first_name: data.profile.first_name,
        last_name: data.profile.last_name,
        phone_number: data.profile.phone_number,
        language: data.profile.language,
        company_id: data.profile.company_id,
        app_role: data.profile.app_role as UserProfile['app_role'],
        status: data.profile.status as UserProfile['status'],
        is_representative: data.profile.is_representative,
        title: data.profile.title,
        company: data.profile.company ? {
          id: data.profile.company.id,
          name: data.profile.company.name,
          type: data.profile.company.type as Company['type'],
          default_language: data.profile.company.default_language,
          logo_attachment_id: data.profile.company.logo_attachment_id,
        } : null,
      } as UserProfile;

      setProfile(userProfile);
      componentLogger.debug('useCurrentUserProfile', 'Profile loaded successfully', {
        userId: userProfile.id,
        hasCompany: !!userProfile.company,
      });
    } catch (err) {
      const errorObj = err as Error;
      setError(errorObj);
      componentLogger.error('useCurrentUserProfile', 'Failed to fetch profile', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    refetch: fetchProfile,
  };
}
